import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import trackService from '../services/trackService';
import type { Track } from '../services/trackService';
import TrackForm from '../components/Tracks/TrackForm';
import axios from 'axios';

/**
 * Edit Track page component
 */
const EditTrack: React.FC = () => {
  const { trackId } = useParams<{ trackId: string }>();
  const navigate = useNavigate();
  
  const [track, setTrack] = useState<Track | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrack = async () => {
      if (!trackId) {
        setError('Track ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const trackData = await trackService.getTrack(trackId);
        
        // Ensure numeric values are properly parsed
        if (trackData) {
          // Convert costPrice and salesPrice to numbers if they're strings
          const parsedTrack = {
            ...trackData,
            costPrice: typeof trackData.costPrice === 'string' 
              ? parseFloat(trackData.costPrice) 
              : trackData.costPrice,
            salesPrice: typeof trackData.salesPrice === 'string' 
              ? parseFloat(trackData.salesPrice) 
              : trackData.salesPrice
          };
          
          setTrack(parsedTrack);
        } else {
          setTrack(trackData);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching track:', err);
        if (axios.isAxiosError(err) && err.response) {
          setError(`Failed to load track: ${err.response.status} ${err.response.statusText}. ${err.response.data?.error || ''}`);
        } else {
          setError('Failed to load track. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTrack();
  }, [trackId]);

  const handleSubmit = async (trackData: Partial<Track>) => {
    if (!trackId) return;
    
    try {
      await trackService.updateTrack(trackId, trackData);
      navigate(`/tracks/${trackId}`);
    } catch (error) {
      console.error('Error updating track:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(`/tracks/${trackId}`);
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Edit Track</h1>
      
      {loading ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center p-lg">
            <div className="spinner"></div>
            <p className="mt-md">Loading track...</p>
          </div>
        </div>
      ) : error ? (
        <div className="card">
          <div className="bg-error p-md" style={{ borderRadius: 'var(--border-radius-md)' }}>
            <h2 className="text-white mb-sm" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold' }}>Error!</h2>
            <p className="text-white">{error}</p>
          </div>
        </div>
      ) : track ? (
        <div className="card">
          <TrackForm 
            initialData={track}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEditing={true}
          />
        </div>
      ) : (
        <div className="card">
          <p className="text-center p-md">Track not found</p>
        </div>
      )}
    </div>
  );
};

export default EditTrack;
